/// <reference path="../libs.ts" />

import { DevicePreviewPreviewStateChangeEvent, TelemetryEvents } from '../../common/telemetryEvents/TelemetryEvents';
import { FeatureFlags } from '../FeatureFlags';
import { DeviceType } from '../models/DeviceType';
import { IPreviewDeviceSize, IPreviewDeviceSizes } from '../models/IPreviewDeviceSize';
import { ISize } from '../models/ISize';
import { Module } from '../Module';
import { IWorkbookActions } from '../ServerApi/types/IWorkbookActions';
import { TelemetryService } from './TelemetryService';

export const DeviceSizes:IPreviewDeviceSizes = {
  phone: {
    width: '375px',
    height: '667px'
  },
  tablet: {
    width: '1024px',
    height: '768px'
  },
  desktop: {
    width: '1024px',
    height: '800px'
  }
};

export const MIN_PHONE_ZOOM_RATIO = .25;

export class DevicePreviewService {
  public static $inject = [
    'TelemetryService'
  ];

  private previewDevice:DeviceType = 'phone';

  constructor(
    private telemetryService: TelemetryService) {}

  public getPreviewDevice():DeviceType {
    return this.previewDevice;
  }

  public changePreviewDevice(device:DeviceType):DeviceType {
    if (this.previewDevice !== device) {
      this.telemetryService.sendEvent<DevicePreviewPreviewStateChangeEvent>({
        type: TelemetryEvents.DevicePreviewPreviewStateChange,
        preview_device_type: device
      });

      this.previewDevice = device;
      return this.previewDevice;
    }
    return null;
  }

  public getSize():IPreviewDeviceSize {
    return DeviceSizes[this.previewDevice];
  }

  private hasWebAuthoringPermissionOnWorkbook(workbookActions: IWorkbookActions): boolean {
    return workbookActions['launchWebAuthoring'];
  }

  // This is used to show the control to Preview Device Layouts so as not to clutter up the UI unless needed
  public isEnabledWithWorkbookPermission(workbookActions: IWorkbookActions): boolean {
    return this.isEnabled() && this.hasWebAuthoringPermissionOnWorkbook(workbookActions);
  }

  public isEnabled(): boolean {
    return FeatureFlags.PreviewDeviceLayouts;
  }

  // To mimic how a dashboard is actually displayed on a phone,
  // we currently zoom out the Viz up to 25% of the original viz size.
  // getShrunkenSizeForPhone is used to calculate the ultimate Viz size when there has been shrinking.
  public getShrunkenSizeForPhone(sheetSize: tb.ISheetSize): ISize | null {
    const minSize = sheetSize.minSize;

    if (this.getPreviewDevice() === 'phone' && minSize) {
      const origWidth:number = parseInt(this.getSize().width, 10);
      const origHeight:number = parseInt(this.getSize().height, 10);

      if (minSize.width > origWidth) {
        const zoomRatio = Math.max(origWidth/minSize.width, MIN_PHONE_ZOOM_RATIO);
        // Iframe size should always be at least the device size for preview purposes.
        const newWidth = Math.max(minSize.width * zoomRatio, origWidth);
        const newHeight = Math.max(minSize.height * zoomRatio, origHeight);
        return {
          width: newWidth,
          height: newHeight
        };
      }
    }

    return null;
  }
}

Module.instance().service('DevicePreviewService', DevicePreviewService);
